import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const ROLES = ["Aşçı", "Aşçı Yardımcısı", "Garson", "Şoför", "Bulaşıkçı", "Depo", "Muhasebe", "Yönetici", "Personel"];
const EMPTY = { name: "", role: "Aşçı", phone: "", national_id: "", sgk_no: "", start_date: "", salary: "", active: true, annual_leave_days: 14 };

export default function StaffForm({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(item ? { ...EMPTY, ...item, salary: item.salary } : EMPTY);
  }, [open, item]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Ad soyad gerekli"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, role: form.role, phone: form.phone, national_id: form.national_id, sgk_no: form.sgk_no,
        start_date: form.start_date, salary: parseFloat(form.salary) || 0, active: form.active,
        annual_leave_days: parseInt(form.annual_leave_days, 10) || 0 };
      if (item) { await api.put(`/staff/${item.id}`, payload); toast.success("Personel güncellendi"); }
      else { await api.post("/staff", payload); toast.success("Personel eklendi"); }
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{item ? "Personel Düzenle" : "Yeni Personel"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <F label="Ad Soyad *" className="sm:col-span-2"><Input value={form.name} onChange={set("name")} data-testid="staff-form-name" /></F>
          <F label="Görev">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="staff-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Telefon"><Input value={form.phone} onChange={set("phone")} data-testid="staff-form-phone" /></F>
          <F label="TC Kimlik No"><Input value={form.national_id} onChange={set("national_id")} data-testid="staff-form-national-id" className="font-mono" /></F>
          <F label="SGK Sicil No"><Input value={form.sgk_no} onChange={set("sgk_no")} data-testid="staff-form-sgk" className="font-mono" /></F>
          <F label="İşe Giriş Tarihi"><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="staff-form-start" className="font-mono" /></F>
          <F label="Aylık Maaş (₺)"><Input type="number" value={form.salary} onChange={set("salary")} data-testid="staff-form-salary" className="font-mono" placeholder="0" /></F>
          <F label="Yıllık İzin Hakkı (gün)"><Input type="number" value={form.annual_leave_days} onChange={set("annual_leave_days")} data-testid="staff-form-leave-days" className="font-mono" /></F>
          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="staff-form-active" />
            <Label className="text-sm">Aktif personel (maaş tahakkukuna dahil)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="staff-form-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
